import type { ClientErrorBodyDto } from '@/generated/api';
import { endpoints } from '@/lib/endpoints';
import { fetchClient } from '@/lib/openapi-client';

/** A page caught in a loop would otherwise post the same error forever. */
const MAX_REPORTS_PER_PAGE_LOAD = 10;

const reported = new Set<string>();

/** For the tests: forget what was reported. */
export function resetReportedErrors(): void {
  reported.clear();
}

function describe(error: unknown): Pick<ClientErrorBodyDto, 'name' | 'message' | 'stack'> {
  if (error instanceof Error) {
    return { name: error.name, message: error.message || error.name, stack: error.stack };
  }
  if (typeof error === 'string' && error.length > 0) {
    return { message: error };
  }
  return { message: 'Unknown error' };
}

/**
 * Sends an error the page caught to the API, which reports it to PostHog
 * Error Tracking: the browser never talks to PostHog itself. Each error is
 * sent once per page load, and only so many of them. Never throws.
 */
export function reportClientError(error: unknown): void {
  const { name, message, stack } = describe(error);
  const key = `${name ?? ''}:${message}`;
  if (reported.has(key) || reported.size >= MAX_REPORTS_PER_PAGE_LOAD) {
    return;
  }
  reported.add(key);

  const body: ClientErrorBodyDto = {
    name: name?.slice(0, 100),
    message: message.slice(0, 1000),
    stack: stack?.slice(0, 8000),
    // The path only: a query string can carry a code or an id.
    page: window.location.pathname.slice(0, 300),
    shell: import.meta.env.APP_SHELL === 'runner' ? 'android' : 'web',
    release: import.meta.env.RELEASE || undefined,
  };

  void fetchClient.POST(endpoints.clientErrors, { body }).catch(() => undefined);
}

/** Errors nothing caught: a failed script, a promise nobody awaited. */
export function reportUncaughtErrors(): void {
  window.addEventListener('error', (event) => reportClientError(event.error ?? event.message));
  window.addEventListener('unhandledrejection', (event) => reportClientError(event.reason));
}
